import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  Grid,
  GridColumn as Column,
  GridToolbar,
} from "@progress/kendo-react-grid";
import {
  CustomActionsCell,
  CustomCell,
  initialDataState,
  CustomPriority,
  HeaderCustomCell,
  getColumnWidthForStaticTaskList,
  getCurrentMediaSize,
} from "./task-util";
import { process } from "@progress/kendo-data-query";
import { TaskGridFilter } from "../common-components/task-grid-filter";
import PropTypes from "prop-types";
import { FluentInput } from "../../../../../components/FluentUI/Fluent-Input";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import _ from "lodash";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import {
  setGroupIds,
  setExpandedState,
  getGroupIds,
} from "@progress/kendo-react-data-tools";
import { LoginContext } from "../../../../../services/api/authentication-api";
import { useHistory } from "react-router-dom";

const processWithGroups = (data, dataState) => {
  const newDataState = process(data, dataState);
  setGroupIds({ data: newDataState.data, group: dataState.group });
  return newDataState;
};

export const TaskGrid = ({ data, GetTasks, type }) => {
  const _export = useRef(null);
  const user = useContext(LoginContext);  
  const labels = user?.currentUser?.objectLabels;
  const history = useHistory();
  const savedGridDefination = JSON.parse(localStorage.getItem("gridStateStatic_"+user?.userID));
  const savedGridColumnDefination = JSON.parse(localStorage.getItem("gridColumnsStatic_"+user?.userID));
  const [savedSearch, setSavedSearch] = useState(localStorage.getItem("gridSearchStatic_"+user?.userID) ?? "");

  const [dataState, setDataState] = useState(savedGridDefination ?? initialDataState);
  const [dataResult, setDataResult] = useState(process([], dataState));
  const [collapsedState, setCollapsedState] = useState([]);
  const [mediaSize, setMediaSize] = useState(getCurrentMediaSize);

  const adjustedColumns = useMemo(() => {
    return data?.allColumns?.map((column) => ({
      ...column,
      width: getColumnWidthForStaticTaskList(column?.title ?? "default", mediaSize),
    }));
  }, [mediaSize, data.allColumns]);

  const [columns, setColumns] = useState(savedGridColumnDefination ?? adjustedColumns);

  useEffect(() => {
    const handleResize = () => {
      const newSize = getCurrentMediaSize();
      setMediaSize((prevSize) => (prevSize !== newSize ? newSize : prevSize));
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);  

  const onDataStateChange = (e) => {
    if (e.dataState.group) {
      e.dataState.group = e.dataState.group.filter(
        (obj, index) =>
          e.dataState.group.findIndex((item) => item.field === obj.field) ===
          index
      );
    }

    if (e.dataState.sort) {
      e.dataState.sort = e.dataState.sort.map((sort) => {
        if (sort.field === "Priority") {
          return {
            ...sort,
            compare: (a, b) => CustomPriority(a, b, sort.dir),
          };
        }
        return sort;
      });
    }
    let tempSkip = e.dataState.skip;
    e.dataState.skip = 0;    
    localStorage.setItem('gridStateStatic_'+user?.userID, JSON.stringify(e.dataState));  
    e.dataState.skip = tempSkip;
    setDataState(e.dataState);
    setDataResult(processWithGroups(data.data, e.dataState));
  };

  const onExpandChange = useCallback(
    (event) => {
      const item = event.dataItem;
      if (item.groupId) {
        const collapsedIds = !event.value
          ? [...collapsedState, item.groupId]
          : collapsedState.filter((groupId) => groupId !== item.groupId);
        setCollapsedState(collapsedIds);
      }
    },
    [collapsedState]
  );

  const openStandardTask = (type, taskId) => {
    let location = {
      pathname: `/tasks/edit/std/${taskId}${type === "view" ? "/view" : ""}`,
    };
    history.push(location);
  };

  const onRowClick = (event) => {
    const { dataItem } = event;
  
    const isEditable = type === "Active" && dataItem.Permission !== "View";
    const navigationType = isEditable ? "edit" : "view";
  
    openStandardTask(navigationType, dataItem.Id);
  };

  const rowRender = (trElement, props) => {
    const trProps = {
      ...trElement.props,
      style: {
        ...trElement.props.style,
        cursor: "pointer",
      },
    };
  
    return React.cloneElement(trElement, trProps, trElement.props.children);
  };

  const onGroupsToggle = useCallback(() => {
    const dataStateGroups = processWithGroups(data.data, {
      group: dataState.group,
    });
    setCollapsedState(
      collapsedState.length ? [] : getGroupIds({ data: dataStateGroups.data })
    );
  }, [collapsedState, data.data, dataState]);

  const expandedResult = setExpandedState({
    data: dataResult.data,
    collapsedIds: collapsedState,
  });

  const onColumnReorder = (event) => {
    let col = event.columns
      .filter((column) => column.title !== "Actions")
      .sort((a, b) => a.orderIndex - b.orderIndex);
    setColumns(col);
    localStorage.setItem("gridColumnsStatic_"+user?.userID, JSON.stringify(col));
  };

  const onColumnResize = (event) => {
    let col = event.columns
      .filter((column) => column.title !== "Actions")
      .sort((a, b) => a.orderIndex - b.orderIndex);
    setColumns(col);
    localStorage.setItem("gridColumnsStatic_"+user?.userID, JSON.stringify(col));
  };

  const resetDataState = () => {
    setDataState(initialDataState);
    setDataResult(process(data.data, initialDataState));    
    setColumns(adjustedColumns);
    setSavedSearch("");
    localStorage.removeItem('gridStateStatic_'+user?.userID);
    localStorage.removeItem('gridColumnsStatic_'+user?.userID);
    localStorage.removeItem('gridSearchStatic_'+user?.userID);
  };

  useEffect(() => {
    const firstDiv = document.querySelector('th[aria-sort="none"]');
    if (firstDiv) {
      firstDiv.firstChild.firstChild.firstChild.style.marginLeft = "1rem";
    }

    setDataResult(process(data.data, dataState));
  }, []);

  const FilterData = (e) => {
    let value = e.target.value;
    let modifiedDataState = { ...dataState };

    modifiedDataState.filter = {
      logic: "or",
      filters: [
        {
          field: "Name",
          operator: "contains",
          value: value,
        },
        {
          field: "Project",
          operator: "contains",
          value: value,
        },
        {
          field: "Priority",
          operator: "contains",
          value: value,
        },
        {
          field: "Status",
          operator: "contains",
          value: value,
        },
        {
          field: "SearchableMembers",
          operator: "contains",
          value: value,
        },
        {
          field: "LastActivity",
          operator: "contains",
          value: value,
        },
        {
          field: "Permission",
          operator: "contains",
          value: value,
        },
        {
          field: "SearchableDue",
          operator: "contains",
          value: value,
        },
      ],
    };

    let tempSkip = modifiedDataState.skip;
    modifiedDataState.skip = 0;    
    localStorage.setItem('gridStateStatic_'+user?.userID, JSON.stringify(modifiedDataState));  
    localStorage.setItem('gridSearchStatic_'+user?.userID, value);
    modifiedDataState.skip = tempSkip;

    setDataState(modifiedDataState);
    setDataResult(process(data.data, modifiedDataState));
  };

  const debouncedFilter = _.debounce(FilterData, 200);

  const formatExportData = (dataItems, labels) => {
    return dataItems.map(item => {
      const formattedItem = { ...item };
      
      if (labels) {
        // Format LastActivity field with label replacements
        if (formattedItem.LastActivity) {
          let lastActivity = formattedItem.LastActivity;
          if (labels.ProjectSingular) {
            lastActivity = lastActivity.replaceAll("Project", labels.ProjectSingular);
          }
          if (labels.TaskSingular) {
            lastActivity = lastActivity.replaceAll("Task", labels.TaskSingular);
          }
          if (labels.FormSingular) {
            lastActivity = lastActivity.replaceAll("Form", labels.FormSingular);
          }
          formattedItem.LastActivity = lastActivity;
        }
      }
      
      return formattedItem;
    });
  };
  
  const excelExport = async () => {
    if (_export.current !== null) {
      let exportData = { data: data?.data, total: data?.data.length };
      if (dataState.filter || dataState.group) {
        let dState = { ...dataState };
        dState.take = dataResult.length;
        exportData = process(data?.data, dState);
      }
  
      if (dataState.group && exportData.data) {
        const flattenGroupedData = (group) =>
          group.reduce((acc, item) => {
            if (item.items) {
              return acc.concat(flattenGroupedData(item.items));
            }
            return acc.concat(item);
          }, []);
  
        exportData.data = flattenGroupedData(exportData.data);
      }
       exportData.data = formatExportData(exportData.data, labels);
      await _export.current.save(exportData);
    }
  };

  return (
    <div className="static-list-container">
      <ExcelExport data={data.data} ref={_export}>
        <ExcelExportColumn field="Name" />
        <ExcelExportColumn field="Project" title={labels?.ProjectSingular ?? "Project"} />
        <ExcelExportColumn field="Priority" />
        <ExcelExportColumn field="Status" />
        <ExcelExportColumn field="LastActivity" title="LastActivity" />
        <ExcelExportColumn field="Permission" />
        <ExcelExportColumn field="SearchableMembers" title="Members" />
        <ExcelExportColumn field="SearchableDue" title="Due" />
      </ExcelExport>
      <Grid
        data={expandedResult}
        pageable={{
          info: true,
          pageSizes: [10, 25, 50, 75, 100],
        }}
        total={dataResult.total}
        groupable={true}
        reorderable={true}
        resizable={true}
        {...dataState}
        onDataStateChange={onDataStateChange}
        onColumnReorder={onColumnReorder}
        onColumnResize={onColumnResize}
        expandField="expanded"
        onExpandChange={onExpandChange}
        onRowClick={onRowClick}
        rowRender={rowRender}
        cells={{
          group: {
            groupHeader: (props) => HeaderCustomCell(props, user),
          },
        }}
      >
        <GridToolbar>
          <div className="task-grid-toolbar">
            <div className="toolbar-left">
              <FluentInput
                placeholder={`Search in all ${type} ${labels?.TaskPlural ?? 'Tasks'}`}
                defaultValue={savedSearch}
                contentBefore={
                  <FluentUiIcon
                    name={"SearchRegular"}
                    color={"#0078D4"}
                    size={14}
                  />
                }
                onChange={(e) => debouncedFilter(e)}
              />
              <FluentButton
                text="Export to XLS"
                onClick={excelExport}
              ></FluentButton>
            </div>
            <div className="toolbar-right">
              <FluentButton
                text="Reset to Default"
                icon={
                  <FluentUiIcon
                    name={"ArrowResetRegular"}
                    color={"#242424"}
                    size={20}
                  />
                }
                onClick={resetDataState}
              ></FluentButton>
              <FluentButton
                text="Refresh"
                icon={
                  <FluentUiIcon
                    name={"ArrowClockwiseRegular"}
                    color={"#242424"}
                    size={20}
                  />
                }
                onClick={GetTasks}
              ></FluentButton>
            </div>
          </div>
        </GridToolbar>
        {columns?.map((column, index) => (
          <Column
            key={column.title}
            field={`${column.title}`}
            title={`${column.title == "Project" ? (labels?.ProjectSingular ?? "Project") : column.title}`}
            orderIndex={index}
            locked={column.locked}
            hidden={column.hidden}
            groupable={column.groupable}
            filter="date"   
            width={column?.width ?? "auto"}
            data-testid={`column-${column.title}`}
            columnMenu={
              column.filterable && column.sortable
                ? (props) => {
                    return TaskGridFilter(props, data);
                  }
                : null
            }
            cells={{
              data: (props) => CustomCell(column.title, props, labels),
            }}
          />
        ))}
        <Column
          reorderable={false}
          orderIndex={adjustedColumns?.length}
          field=""
          title="Actions"
          cells={{
            data: (props) => CustomActionsCell(props, GetTasks),
          }}
        />
      </Grid>
    </div>
  );
};

TaskGrid.propTypes = {
  data: PropTypes.object,
  GetTasks: PropTypes.func,
  type: PropTypes.string,
};
